import React, { useEffect, useRef, useState } from "react";
import { PlayCircleFilled, PictureOutlined, CheckCircleFilled } from "@ant-design/icons";
import { theme } from "antd";
import type { MediaEntry } from "./types";

// Every thumbnail is a `/media/proxy` URL built by MediaService.ProxyUrl — the NAS needs a
// sharing_sid cookie an <img> can't attach — so an occasional failed load is more likely a transient
// NAS hiccup than a real bug. One automatic retry clears most of those; a real placeholder for
// anything that still fails beats a browser's broken-image icon.
const RETRY_DELAY_MS = 1500;

// Touch has no hover, so the corner tick can't be the way into selection mode there — holding a tile
// this long selects it instead, the gesture every phone gallery already uses. The tolerance exists
// because a finger that has started scrolling the grid must not end up selecting whatever it began on.
const LONG_PRESS_MS = 450;
const LONG_PRESS_MOVE_TOLERANCE_PX = 10;

const Thumb: React.FC<{ src: string; alt: string; style: React.CSSProperties }> = ({ src, alt, style }) => {
    const [attempt, setAttempt] = useState(0);
    const [failed, setFailed] = useState(false);
    const retried = useRef(false);

    useEffect(() => {
        retried.current = false;
        setFailed(false);
        setAttempt(0);
    }, [src]);

    if (failed) {
        return (
            <div style={{ ...style, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--ui-surface2)" }}>
                <PictureOutlined style={{ fontSize: 20, color: "var(--ui-text3)" }} />
            </div>
        );
    }

    return (
        <img
            // key forces a real remount (fresh network request) on retry — the src itself is left
            // untouched rather than appending a cache-busting param, since Synology's API has a
            // history of rejecting unrecognized query params on some endpoints (see SynologyShareClient
            // notes) and this endpoint's tolerance for extras isn't confirmed.
            key={attempt}
            src={src}
            alt={alt}
            // Only fetch what's near the viewport. Every thumbnail is a proxied NAS request, so a 500-item
            // album otherwise fires 500 of them the moment the grid mounts — they queue behind each other
            // (six at a time per host) and the first screenful, the only part anyone is looking at, ends up
            // waiting behind items far below the fold. decoding="async" keeps decode off the main thread so
            // scrolling stays smooth as they arrive.
            loading="lazy"
            decoding="async"
            style={style}
            onError={() => {
                if (!retried.current) {
                    retried.current = true;
                    setTimeout(() => setAttempt(a => a + 1), RETRY_DELAY_MS);
                } else {
                    setFailed(true);
                }
            }}
        />
    );
};

export const formatDuration = (ms: number | null) => {
    if (!ms) return null;
    const totalSeconds = Math.round(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

/** Square thumbnail tile for a MediaEntry — split back/front thumbs for a pair, play-icon overlay
 * for video, duration badge. Shared by the album grid and the map's cluster overview grid so both
 * stay visually consistent.
 *
 * selectionMode/selected add the album page's batch-download picker: a checkbox in the corner and a
 * highlighted ring. No per-tile download button here — between the split thumbs, play icon, and
 * duration badge the tile is already full, and the player modal plus the selection toolbar cover
 * single- and multi-item download without crowding this grid. */
export const MediaThumbTile: React.FC<{
    entry: MediaEntry;
    borderRadius: number;
    onClick: () => void;
    selectionMode?: boolean;
    selected?: boolean;
    /** Given, the tick becomes an always-available control: it fades in on hover even outside selection
     *  mode, and clicking it selects this tile — entering selection mode if that's where you started.
     *  That way picking things doesn't require finding the toolbar button first. */
    onToggleSelect?: () => void;
}> = ({ entry, borderRadius, onClick, selectionMode = false, selected = false, onToggleSelect }) => {
    const isGerman = navigator.language.toLowerCase().startsWith("de");
    const { token } = theme.useToken();
    const [hovered, setHovered] = useState(false);
    const pointerInside = useRef(false);
    const press = useRef({
        timer: undefined as ReturnType<typeof setTimeout> | undefined,
        x: 0, y: 0, fired: false, touch: false,
    });

    const cancelPress = () => {
        clearTimeout(press.current.timer);
        press.current.timer = undefined;
    };
    useEffect(() => cancelPress, []);

    const startPress = (event: React.PointerEvent) => {
        press.current.touch = event.pointerType !== "mouse";
        if (!press.current.touch || !onToggleSelect) return;
        press.current = { ...press.current, x: event.clientX, y: event.clientY, fired: false };
        press.current.timer = setTimeout(() => {
            press.current.fired = true;
            // Nothing else moves under the finger at this instant, so the buzz is the only
            // confirmation that the hold registered rather than the tap being ignored.
            if (typeof navigator.vibrate === "function") navigator.vibrate(15);
            onToggleSelect();
        }, LONG_PRESS_MS);
    };

    const movePress = (event: React.PointerEvent) => {
        if (!press.current.timer) return;
        if (Math.abs(event.clientX - press.current.x) > LONG_PRESS_MOVE_TOLERANCE_PX ||
            Math.abs(event.clientY - press.current.y) > LONG_PRESS_MOVE_TOLERANCE_PX) {
            cancelPress();
        }
    };

    // Hover-reveal is a mouse affordance only. Touch fires enter/leave on tap, which made the tick
    // flicker in unbidden on a phone — the long press replaces it there.
    const tickVisible = selectionMode || (Boolean(onToggleSelect) && hovered);
    const mediaTypeLabel = entry.mediaType === "video" ? "Video" : "Foto";
    let tileLabel: string;
    let toggleLabel: string;
    if (isGerman) {
        tileLabel = `${mediaTypeLabel} vom ${entry.timestamp} ${selectionMode ? "auswählen" : "öffnen"}`;
        toggleLabel = `Element vom ${entry.timestamp} ${selected ? "abwählen" : "auswählen"}`;
    } else {
        tileLabel = `${selectionMode ? "Select" : "Open"} ${entry.mediaType} from ${entry.timestamp}`;
        toggleLabel = `${selected ? "Deselect" : "Select"} item from ${entry.timestamp}`;
    }

    return (
        <div
            style={{ position: "relative", aspectRatio: "1", borderRadius, overflow: "hidden" }}
            onPointerEnter={event => {
                if (event.pointerType === "mouse") {
                    pointerInside.current = true;
                    setHovered(true);
                }
            }}
            onPointerLeave={() => {
                pointerInside.current = false;
                setHovered(false);
            }}
        >
        <button
            type="button"
            aria-pressed={selectionMode ? selected : undefined}
            aria-label={tileLabel}
            onClick={() => {
                // A long press has already acted; swallow the click the browser sends afterwards so
                // the same gesture doesn't also open the viewer.
                if (press.current.fired) { press.current.fired = false; return; }
                onClick();
            }}
            onPointerDown={startPress}
            onPointerMove={movePress}
            onPointerUp={cancelPress}
            onPointerCancel={cancelPress}
            onPointerLeave={cancelPress}
            // Only for touch: a right-click on desktop should still offer the image menu.
            onContextMenu={event => { if (press.current.touch) event.preventDefault(); }}
            style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                padding: 0,
                border: 0,
                color: "inherit",
                font: "inherit",
                textAlign: "left",
                cursor: "pointer",
                background: token.colorFillQuaternary,
                display: "flex",
                outline: selected ? `3px solid ${token.colorPrimary}` : undefined,
                outlineOffset: selected ? -3 : 0,
                // Stops the hold turning into a text/image selection or iOS's save-image callout.
                userSelect: "none",
                WebkitUserSelect: "none",
                WebkitTouchCallout: "none",
                touchAction: "manipulation",
            }}
        >
            {entry.kind === "pair" && entry.frontThumbnailUrl ? (
                <>
                    <Thumb src={entry.thumbnailUrl} alt="" style={{ width: "50%", height: "100%", objectFit: "cover" }} />
                    <Thumb src={entry.frontThumbnailUrl} alt="" style={{ width: "50%", height: "100%", objectFit: "cover" }} />
                </>
            ) : (
                <Thumb src={entry.thumbnailUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            )}

            {entry.mediaType === "video" && (
                <PlayCircleFilled style={{
                    position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
                    fontSize: 32, color: token.colorTextLightSolid, filter: `drop-shadow(0 1px 4px ${token.colorBgMask})`,
                }} />
            )}

            {formatDuration(entry.durationMs) && (
                <span style={{
                    position: "absolute", bottom: 4, right: 6,
                    background: token.colorBgMask, color: token.colorTextLightSolid, fontSize: 12,
                    padding: "1px 6px", borderRadius: 4,
                }}>
                    {formatDuration(entry.durationMs)}
                </span>
            )}

        </button>

            {(selectionMode || onToggleSelect) && (
                <button
                    type="button"
                    aria-label={toggleLabel}
                    aria-pressed={selected}
                    onClick={event => {
                        // Stops the tile's own onClick from opening the viewer — clicking the tick is a
                        // selection gesture, not a "view this" gesture.
                        event.stopPropagation();
                        onToggleSelect?.();
                    }}
                    onFocus={() => setHovered(true)}
                    onBlur={() => { if (!pointerInside.current) setHovered(false); }}
                    style={{
                        position: "absolute", top: 0, left: 0, width: 44, height: 44, fontSize: 20,
                        color: token.colorTextLightSolid,
                        border: 0, padding: 0, background: selected ? token.colorPrimary : "transparent", cursor: "pointer",
                        filter: `drop-shadow(0 1px 3px ${token.colorBgMask})`,
                        opacity: tickVisible ? 1 : 0,
                        transition: "opacity 0.15s",
                        // Unhovered and out of selection mode it's invisible, so it must not swallow
                        // clicks meant for the tile underneath.
                        pointerEvents: tickVisible ? "auto" : "none",
                    }}
                ><CheckCircleFilled /></button>
            )}
        </div>
    );
};
